import { useEffect, useRef } from 'react';

/** Refresh this long before a signed URL stops working, so a thumbnail never goes broken. */
const REFRESH_LEAD_MS = 30_000;

/**
 * Earliest attachment URL expiry still ahead of `now` and not in `handled`, as epoch ms, or
 * null when nothing needs scheduling. Already-expired URLs return null: a mount after expiry
 * refetches through react-query staleness instead of scheduling a timer in the past.
 */
export const earliestPendingExpiry = (
  attachments: { expiresAt?: string | null }[],
  now: number,
  handled: ReadonlySet<number>
): number | null => {
  const pending = attachments
    .map((attachment) => (attachment.expiresAt ? new Date(attachment.expiresAt).getTime() : Number.NaN))
    .filter((at) => !Number.isNaN(at) && at > now && !handled.has(at));
  return pending.length > 0 ? Math.min(...pending) : null;
};

/**
 * Calls `onUrlsExpired` shortly before the earliest displayed attachment URL expires, so a
 * list left open past its signed URLs' lifetime refetches instead of showing broken previews
 * and dead links. Fires once per expiry value: if the refetch hands out the same expiry again
 * (clock skew) it is not repeated. `onUrlsExpired` may change every render; the latest one is
 * used when the timer fires. Without a callback nothing is scheduled.
 *
 * Shared by web (`noddi-ui-web` note surfaces) and the worker app — this module is the single
 * home; do not fork it per platform.
 */
export const useNoteAttachmentUrlRefresh = (
  notes: { attachments: { expiresAt?: string | null }[] }[],
  onUrlsExpired: (() => void | Promise<unknown>) | undefined
) => {
  const handledExpiries = useRef<Set<number>>(new Set());
  const onUrlsExpiredRef = useRef(onUrlsExpired);
  useEffect(() => {
    onUrlsExpiredRef.current = onUrlsExpired;
  });

  // Only `notes` re-arms the timer; the callback is read through the ref, so a new identity
  // every render does not clear and recreate a timer that targets the same instant.
  useEffect(() => {
    if (!onUrlsExpiredRef.current) {
      return;
    }
    const next = earliestPendingExpiry(
      notes.flatMap((note) => note.attachments),
      Date.now(),
      handledExpiries.current
    );
    if (next === null) {
      return;
    }
    const timer = setTimeout(
      () => {
        handledExpiries.current.add(next);
        void onUrlsExpiredRef.current?.();
      },
      Math.max(next - REFRESH_LEAD_MS - Date.now(), 0)
    );
    return () => clearTimeout(timer);
  }, [notes]);
};
